import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Divider,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CountryFlag from "./CountryFlag";
import { VAT_COUNTRY_OPTIONS } from "../../utils/vatCountryOptions";
import {
  buildVatNumber,
  normalizeVatNumber,
  parseVatFields,
} from "../../utils/vatNumberUtils";

const FLAG_IMG_PROPS = {
  style: {
    width: 18,
    height: 13,
    objectFit: "cover",
    borderRadius: 2,
    display: "block",
    flexShrink: 0,
  },
};

/** Shared select layout: full input height, tighter gap before dropdown arrow. */
const COMPACT_COUNTRY_SELECT_SX = {
  height: "100%",
  alignSelf: "stretch",
  flexShrink: 0,
  minWidth: 74,
  maxWidth: 88,
  width: 82,
  "& .MuiOutlinedInput-root": {
    height: "100%",
    minHeight: "unset",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    py: 0,
    pl: 1,
    pr: "18px !important",
    minHeight: "unset",
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": {
    right: 2,
    width: 18,
    height: 18,
    top: "calc(50% - 9px)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
};

const VARIANT_STYLES = {
  application: {
    wrapper: {},
    field: {
      borderRadius: "8px",
      backgroundColor: "#dce6f7",
      "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        pl: 0,
        minHeight: 56,
        "& fieldset": {
          border: "none",
        },
      },
      "& .MuiInputBase-input": {
        color: "#0b3d87",
        fontStyle: "italic",
        py: "12px",
        px: "14px",
      },
      "& .MuiInputLabel-root": {
        color: "#0b3d87",
        fontStyle: "italic",
        fontSize: "0.95rem",
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: "#0b3d87",
      },
      "& .MuiFormHelperText-root": {
        color: "#e00036",
        marginLeft: 0,
      },
      "& .MuiInputAdornment-root": {
        height: "auto",
        maxHeight: "none",
        alignSelf: "stretch",
      },
    },
    select: {
      ...COMPACT_COUNTRY_SELECT_SX,
      color: "#0b3d87",
      fontStyle: "italic",
      fontWeight: 600,
    },
    divider: {
      borderColor: "rgba(11, 61, 135, 0.18)",
      alignSelf: "stretch",
      my: 1,
    },
    menuItem: {
      gap: 1,
    },
  },
  vendor: {
    wrapper: {},
    field: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        fontFamily: "Rubik, sans-serif",
        fontSize: { xs: "1.02rem", sm: "1.07rem" },
        borderRadius: "10px",
        backgroundColor: "#fafbff",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        minHeight: { xs: 52, sm: 54 },
        pl: 0,
        "& fieldset": {
          borderColor: "rgba(11, 61, 135, 0.14)",
        },
        "&:hover fieldset": {
          borderColor: "rgba(11, 61, 135, 0.35)",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#0b3d87",
          borderWidth: 1,
        },
      },
      "& .MuiInputBase-input": {
        color: "#0b3d87",
        py: { xs: 1.45, sm: 1.6 },
      },
      "& .MuiFormHelperText-root": {
        fontFamily: "Rubik, sans-serif",
        textAlign: "center",
      },
      "& .MuiInputAdornment-root": {
        height: "auto",
        maxHeight: "none",
        alignSelf: "stretch",
      },
    },
    select: {
      ...COMPACT_COUNTRY_SELECT_SX,
      color: "#0b3d87",
      fontFamily: "Rubik, sans-serif",
      fontWeight: 600,
    },
    divider: {
      borderColor: "rgba(11, 61, 135, 0.18)",
      alignSelf: "stretch",
      my: 1,
    },
    menuItem: {
      gap: 1,
    },
  },
  admin: {
    wrapper: {},
    field: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        pl: 0,
        minHeight: 56,
      },
      "& .MuiInputAdornment-root": {
        height: "auto",
        maxHeight: "none",
        alignSelf: "stretch",
      },
    },
    select: {
      ...COMPACT_COUNTRY_SELECT_SX,
      color: "#0b3d87",
      fontWeight: 600,
    },
    divider: {
      borderColor: "rgba(11, 61, 135, 0.18)",
      alignSelf: "stretch",
      my: 1,
    },
    menuItem: {
      gap: 1,
    },
  },
};

const CountryOptionLabel = ({ code, name, showName = false, compact = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: compact ? 0.5 : 1,
      minWidth: 0,
      overflow: "hidden",
    }}
  >
    <CountryFlag countryCode={code} size="w20" format="webp" imgProps={FLAG_IMG_PROPS} />
    <Typography
      component="span"
      sx={{
        fontWeight: 600,
        fontSize: compact ? "0.8125rem" : "0.875rem",
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      {code}
    </Typography>
    {showName ? (
      <Typography component="span" sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
        {name}
      </Typography>
    ) : null}
  </Box>
);

const VatNumberField = ({
  variant = "application",
  name,
  label,
  value = "",
  onChange,
  error = false,
  helperText = "",
  required = false,
  fullWidth = true,
  id,
  sx = {},
  labelSx,
  showExternalLabel = false,
}) => {
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.application;
  const parsed = parseVatFields(value);
  const [countryCode, setCountryCode] = useState(parsed.countryCode);
  const [body, setBody] = useState(parsed.body);

  useEffect(() => {
    const next = parseVatFields(value);
    setCountryCode(next.countryCode);
    setBody(next.body);
  }, [value]);

  const emitChange = useCallback(
    (nextCountryCode, nextBody) => {
      if (!onChange) {
        return;
      }
      const combined = buildVatNumber(nextCountryCode, nextBody);
      onChange({
        target: {
          name,
          value: combined,
        },
      });
    },
    [name, onChange]
  );

  const handleCountryChange = (event) => {
    const nextCountry = event.target.value;
    setCountryCode(nextCountry);
    emitChange(nextCountry, body);
  };

  const handleBodyChange = (event) => {
    let nextBody = (event.target.value || "").toUpperCase().replace(/[^A-Z0-9\s.\-/]/g, "");
    const compact = normalizeVatNumber(nextBody);

    if (/^[A-Z]{2}[A-Z0-9]{2,}$/.test(compact)) {
      const split = parseVatFields(compact);
      setCountryCode(split.countryCode);
      setBody(split.body);
      emitChange(split.countryCode, split.body);
      return;
    }

    if (compact.length > 14) {
      return;
    }

    setBody(nextBody.trim());
    emitChange(countryCode, nextBody.trim());
  };

  const fieldId = id || `vat-field-${name}`;
  const labelText = required && label && !label.endsWith("*") ? `${label}*` : label;

  const countrySelect = (
    <Select
      value={countryCode}
      onChange={handleCountryChange}
      variant="outlined"
      aria-label="Prefisso paese partita IVA"
      sx={styles.select}
      renderValue={(selected) => (
        <CountryOptionLabel
          compact
          code={selected}
          name={VAT_COUNTRY_OPTIONS.find((item) => item.code === selected)?.name || selected}
        />
      )}
      MenuProps={{
        PaperProps: {
          sx: { maxHeight: 320 },
        },
      }}
    >
      {VAT_COUNTRY_OPTIONS.map((option) => (
        <MenuItem key={option.code} value={option.code} sx={styles.menuItem}>
          <CountryOptionLabel code={option.code} name={option.name} showName />
        </MenuItem>
      ))}
    </Select>
  );

  const inputField = (
    <TextField
      id={fieldId}
      name={name}
      label={showExternalLabel ? undefined : labelText}
      value={body}
      onChange={handleBodyChange}
      error={error}
      helperText={showExternalLabel ? undefined : helperText}
      fullWidth={fullWidth}
      variant="outlined"
      size="medium"
      sx={{ ...styles.field, ...sx }}
      inputProps={{
        maxLength: 18,
        "aria-label": labelText || "Numero partita IVA",
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ mr: 0, height: "100%", maxHeight: "none" }}>
            {countrySelect}
            <Divider orientation="vertical" flexItem sx={styles.divider} />
          </InputAdornment>
        ),
      }}
    />
  );

  if (!showExternalLabel) {
    return inputField;
  }

  return (
    <Box sx={styles.wrapper}>
      {labelText ? (
        <Typography
          component="label"
          htmlFor={fieldId}
          sx={{
            fontFamily: "Rubik, sans-serif",
            fontWeight: 600,
            fontSize: "0.8125rem",
            color: "#0b3d87",
            display: "block",
            mb: 0.75,
            textAlign: "center",
            ...labelSx,
          }}
        >
          {labelText}
        </Typography>
      ) : null}
      {inputField}
      {helperText ? (
        <FormHelperText error={error} sx={{ textAlign: "center", mt: 0.5, mx: 0 }}>
          {helperText}
        </FormHelperText>
      ) : null}
    </Box>
  );
};

export default React.memo(VatNumberField);
